import { connect } from 'react-redux';
import * as Store from '@apply/store';
import React, { useState, useEffect } from 'react';
import DataGrid, { Column, Paging, Pager, ColumnFixing } from 'devextreme-react/data-grid';
import { Tooltip } from '@material-ui/core';
import { ExperienceProjectTypeOptions } from '@apply/constants';
import RadioGroup from 'devextreme-react/radio-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglassHalf, faEdit } from '@fortawesome/free-regular-svg-icons';
import { ActiveExperienceViewTypeOptions } from '../experience/constants/ActiveExperienceViewTypeOptions';
import { ActiveExperienceViewType } from '../experience/constants/ActiveExperienceViewType';

const ExperienceActiveGrid = ({ activeExperiences, onClickAddMoreInformation }) => {
    const [filteredExperiences, set_filteredExperiences] = useState(activeExperiences);
    const [experienceViewType, set_experienceViewType] = useState(ActiveExperienceViewType.NonBFIExperience);

    const allowedPageSizes = [15, 25, 50];
    const defaultPageSize = allowedPageSizes[0];

    const styles = {
        experienceContainer: {
            width: '100%' as const,
        },
        experienceSelector: {
            width: 'auto' as const,
            paddingTop: 5,
            paddingBottom: 5,
            marginLeft: 'auto' as const,
        },
        verticalPadding: {
            paddingTop: 50,
            paddingBottom: 50,
        },
        button: {
            margin: '0 5px 0 0',
            width: '40px',
            padding: '5px',
            borderColor: '#2c5591',
            backgroundColor: 'white',
            color: '#2c5591',
        },
    };

    useEffect(() => {
        switch (experienceViewType) {
            case ActiveExperienceViewType.NonBFIExperience:
                set_filteredExperiences(
                    activeExperiences
                        .filter((e) => e.experienceTypeId == Store.UserStore.Types.ExperienceType.NonBfi)
                        .sort((a, b) => (a.rowNumber > b.rowNumber ? 1 : -1))
                );
                break;
            case ActiveExperienceViewType.BFIExperience:
                set_filteredExperiences(
                    activeExperiences
                        .filter(
                            (e) =>
                                e.experienceTypeId == Store.UserStore.Types.ExperienceType.BfiRepaid ||
                                e.experienceTypeId == Store.UserStore.Types.ExperienceType.BfiOutstanding
                        )
                        .sort((a, b) => (a.rowNumber > b.rowNumber ? 1 : -1))
                );
                break;
            default:
                break;
        }
    }, [activeExperiences, experienceViewType]);

    const onActionRender = (e) => {
        if (
            e.data.experienceTypeId != Store.UserStore.Types.ExperienceType.NonBfi ||
            (e.data.isVerified && e.data.experienceTypeId == Store.UserStore.Types.ExperienceType.NonBfi)
        ) {
            return;
        } else {
            return (
                <div>
                    <button
                        className='button experienceMoreInfoButton'
                        title='Edit Experience'
                        // eslint-disable-next-line
                        onClick={(event) => onClickAddMoreInformation(e.data.id)}
                        disabled={e.data.experienceTypeId !== Store.UserStore.Types.ExperienceType.NonBfi || e.data.isVerified}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </button>
                </div>
            );
        }
    };

    const onRowNumberCellRender = (e) => {
        const requiresVerification = verificationRequired(e);
        const fontColor = getRowRenderStyleType(e);
        return (
            <span style={{ color: fontColor, textAlign: 'right' }}>
                {requiresVerification && (
                    <Tooltip title='Pending verification. You may edit prior to verification using the button at the right.'>
                        <FontAwesomeIcon
                            style={{ paddingRight: '5px' }}
                            icon={faHourglassHalf}
                        />
                    </Tooltip>
                )}
                {e.data.rowNumber + '.'}
            </span>
        );
    };

    const onProjectTypeCellRender = (e) => {
        const fontColor = getRowRenderStyleType(e);
        const option = ExperienceProjectTypeOptions.find((a) => a.id === e.value);
        return <span style={{ color: fontColor }}>{option?.name ?? 'Unknown'}</span>;
    };

    const verificationRequired = (e) => {
        if (
            e.data.experienceTypeId != Store.UserStore.Types.ExperienceType.NonBfi ||
            (e.data.isVerified && e.data.experienceTypeId == Store.UserStore.Types.ExperienceType.NonBfi)
        ) {
            return false;
        } else {
            return true;
        }
    };

    const getRowRenderStyleType = (e) => {
        if (verificationRequired(e)) {
            return 'gray';
        } else {
            return 'black';
        }
    };

    const formatAddress = (e) => {
        if (e.data.street2) {
            return `${e.data.street1} ${e.data.street2}, ${e.data.city}, ${e.data.state} ${e.data.zipCode}`;
        } else {
            return `${e.data.street1}, ${e.data.city}, ${e.data.state} ${e.data.zipCode}`;
        }
    };

    const onAddressCellRender = (e) => {
        const fontColor = getRowRenderStyleType(e);

        if (!e.data) {
            return null;
        }
        if (e.data.street2) {
            return (
                <Tooltip title={formatAddress(e)}>
                    <span style={{ color: fontColor }}>{formatAddress(e)}</span>
                </Tooltip>
            );
        } else {
            return (
                <Tooltip title={formatAddress(e)}>
                    <span style={{ color: fontColor }}>{formatAddress(e)}</span>
                </Tooltip>
            );
        }
    };

    return (
        <>
            <h4>Submitted</h4>
            {activeExperiences?.length > 0 && (
                <>
                    <div
                        className='columns small-12 large-5'
                        style={styles.experienceSelector}
                    >
                        <RadioGroup
                            items={ActiveExperienceViewTypeOptions}
                            valueExpr='id'
                            value={experienceViewType}
                            onValueChanged={(event) => set_experienceViewType(parseInt(event.value))}
                            layout='horizontal'
                        />
                    </div>
                    <div
                        className='columns small-12 large-12'
                        style={styles.experienceContainer}
                    >
                        <DataGrid
                            keyExpr='id'
                            id='experienceTable'
                            dataSource={filteredExperiences}
                            showBorders={true}
                            showRowLines={true}
                            remoteOperations={false}
                            allowColumnResizing={true}
                            allowColumnReordering={false}
                            columnResizingMode={'nextColumn'}
                            columnMinWidth={20}
                            cellHintEnabled={true}
                            width={'auto'}
                        >
                            <ColumnFixing enabled={true} />
                            <Column
                                caption=''
                                cellRender={onRowNumberCellRender}
                                dataField='rowNumber'
                                width={70}
                                alignment='right'
                            />
                            <Column
                                caption='Project Type'
                                dataField='projectTypeId'
                                alignment={'left'}
                                minWidth={130}
                                allowSorting={false}
                                cellRender={onProjectTypeCellRender}
                            />
                            <Column
                                caption='Address'
                                alignment='left'
                                minWidth={120}
                                allowSorting={false}
                                cellRender={onAddressCellRender}
                            />
                            {experienceViewType === ActiveExperienceViewType.NonBFIExperience && (
                                <Column
                                    caption=''
                                    cellRender={onActionRender}
                                    width={80}
                                    alignment='center'
                                />
                            )}
                            <Paging defaultPageSize={defaultPageSize} />
                            <Pager
                                showPageSizeSelector={allowedPageSizes.length > 1}
                                allowedPageSizes={allowedPageSizes}
                                showNavigationButtons={true}
                                showInfo={true}
                                visible={true}
                            />
                        </DataGrid>
                    </div>
                </>
            )}
            {!activeExperiences?.length && (
                <ul>
                    <li>
                        <em>None</em>
                    </li>
                </ul>
            )}
        </>
    );
};

export const mapStateToProps = (state: Store.ApplyRootStateType) => ({
    activeExperiences: state.user?.activeExperiences ?? [],
});

export default connect(mapStateToProps)(ExperienceActiveGrid);
